/**
 * Wandering America landing: scroll-triggered reveals only (no hero parallax).
 */
(function () {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const revealObserved = new WeakSet();
  const heroBackgroundObserved = new WeakSet();
  const pdpGalleryObserved = new WeakSet();
  const tickerObserved = new WeakSet();

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = reject;
      img.src = url;
    });
  }

  function pickRandomItem(items, currentIndex) {
    if (items.length <= 1) return items[0];

    let nextIndex = currentIndex;
    while (nextIndex === currentIndex) {
      nextIndex = Math.floor(Math.random() * items.length);
    }

    return items[nextIndex];
  }

  async function initHeroBackgrounds() {
    const heroBgs = document.querySelectorAll(".wa-cinematic-hero__bg[data-wa-hero-backgrounds]");

    for (const bg of heroBgs) {
      if (heroBackgroundObserved.has(bg)) continue;
      heroBackgroundObserved.add(bg);

      const img = bg.querySelector(".wa-cinematic-hero__bg-img");
      if (!img) continue;

      let urls = [];
      try {
        urls = JSON.parse(bg.dataset.waHeroBackgrounds || "[]").filter(Boolean);
      } catch (_error) {
        urls = [];
      }

      if (!urls.length) continue;

      const available = [];

      for (const [index, url] of urls.entries()) {
        try {
          const loadedUrl = await loadImage(url);
          available.push({ url: loadedUrl, index });
        } catch (_error) {
          // Missing local theme assets are expected while the hero set is being assembled.
        }
      }

      if (available.length) {
        let currentItem = pickRandomItem(available, -1);
        img.src = currentItem.url;
        window.localStorage.setItem("waHeroBackgroundIndex", String(currentItem.index));

        if (!reduceMotion && available.length > 1) {
          window.setInterval(() => {
            const currentAvailableIndex = available.findIndex((item) => item.index === currentItem.index);
            currentItem = pickRandomItem(available, currentAvailableIndex);
            img.src = currentItem.url;
            window.localStorage.setItem("waHeroBackgroundIndex", String(currentItem.index));
          }, 30000);
        }

        return;
      }

      if (bg.dataset.waHeroDefault) {
        img.src = bg.dataset.waHeroDefault;
      }
    }
  }

  function initReveals() {
    if (reduceMotion) {
      document.querySelectorAll(".wa-reveal").forEach((el) => el.classList.add("wa-reveal--visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("wa-reveal--visible");
          io.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    document.querySelectorAll(".wa-reveal").forEach((el) => {
      if (revealObserved.has(el) || el.classList.contains("wa-reveal--visible")) return;
      revealObserved.add(el);
      io.observe(el);
    });
  }

  function initPdpGallerySwipes() {
    document.querySelectorAll(".wa-pdp-showcase__gallery").forEach((gallery) => {
      if (pdpGalleryObserved.has(gallery)) return;
      pdpGalleryObserved.add(gallery);

      const stage = gallery.querySelector(".wa-pdp-showcase__stage");
      const inputs = Array.from(gallery.querySelectorAll(".wa-pdp-showcase__input"));
      if (!stage || inputs.length <= 1) return;

      let pointerId = null;
      let startX = 0;
      let startY = 0;

      const selectIndex = (index) => {
        inputs[index].checked = true;
        inputs[index].dispatchEvent(new Event("change", { bubbles: true }));
      };

      const selectOffset = (offset) => {
        const currentIndex = Math.max(
          inputs.findIndex((input) => input.checked),
          0
        );
        const nextIndex = (currentIndex + offset + inputs.length) % inputs.length;
        selectIndex(nextIndex);
      };

      stage.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        stage.setPointerCapture(pointerId);
      });

      stage.addEventListener("pointerup", (event) => {
        if (event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        pointerId = null;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
        selectOffset(deltaX < 0 ? 1 : -1);
      });

      stage.addEventListener("pointercancel", () => {
        pointerId = null;
      });

      if (!reduceMotion) {
        window.setTimeout(() => {
          if (!document.body.contains(gallery)) return;
          selectOffset(1);

          window.setInterval(() => {
            if (!document.body.contains(gallery)) return;
            selectOffset(1);
          }, 5000);
        }, 7000);
      }
    });
  }

  function initLaunchTickers() {
    document.querySelectorAll(".wa-launch-details__ticker").forEach((ticker) => {
      if (tickerObserved.has(ticker)) return;
      tickerObserved.add(ticker);

      const track = ticker.querySelector(".wa-launch-details__track");
      if (!track) return;

      let pointerId = null;
      let startX = 0;
      let currentX = 0;

      const setOffset = (value) => {
        track.style.setProperty("--wa-launch-drag-x", `${value}px`);
      };

      ticker.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        currentX = 0;
        ticker.classList.add("is-dragging");
        ticker.setPointerCapture(pointerId);
        setOffset(0);
      });

      ticker.addEventListener("pointermove", (event) => {
        if (event.pointerId !== pointerId) return;
        currentX = event.clientX - startX;
        setOffset(currentX);
      });

      const endDrag = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        ticker.classList.remove("is-dragging");
        track.style.removeProperty("--wa-launch-drag-x");
      };

      ticker.addEventListener("pointerup", endDrag);
      ticker.addEventListener("pointercancel", endDrag);
      ticker.addEventListener("lostpointercapture", () => {
        pointerId = null;
        ticker.classList.remove("is-dragging");
        track.style.removeProperty("--wa-launch-drag-x");
      });
      ticker.addEventListener("dragstart", (event) => {
        event.preventDefault();
      });
    });
  }

  function boot() {
    initHeroBackgrounds();
    initPdpGallerySwipes();
    initLaunchTickers();
    initReveals();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
})();
